/**
 * gift-box/index.js — «Коробка для подарка точно по размеру»: the size of
 * the gift in, a net out — previewed closed (isometric) and flat, printed at
 * true size (tiled over several sheets when larger than the paper), or saved
 * as an SVG for a cutting plotter.
 *
 * Third of the holiday tools: shares the print engine of /printable-masks and
 * the patterns, palette helpers and glyph fitting of /letter-banner.
 */
import { THEMES, CRAYONS } from '../printable-masks/catalog.js';
import { build, paint, toSvg } from '../printable-masks/compose.js';
import { PAPERS } from '../printable-masks/sheet.js';
import { ensurePdfLib } from '../printable-masks/pdf.js';
import { boxNet, TYPES } from './box.js';
import { pieceOps, isoSvg, plotterSvg } from './paint.js';
import { buildBoxPdf, planBox } from './pdf.js';

const SLUG = 'gift-box-template';
const CFG = window.__GIFTBOX__ || {};
const T = CFG.t || {};
const t = (k, fb) => (T[k] != null ? T[k] : (fb != null ? fb : k));
const $ = id => document.getElementById(id);

/* Gifts people box most, inner size in mm (L × W × H). Measured on the object
   plus a few millimetres of play — a box made exactly to size will not close
   over a mug with a handle. */
const PRESETS = {
    mug: [125, 95, 105], phone: [170, 85, 25], sweets: [120, 120, 40], jewelry: [50, 50, 35],
    cupcake: [90, 90, 85], cookie: [150, 100, 30], card: [160, 115, 15], cube: [80, 80, 80],
};
const PATTERNS = ['none', 'dots', 'stripes', 'stars', 'confetti'];
const FONTS = ['montserrat', 'russoone', 'lobster', 'comfortaa', 'caveat', 'pacifico', 'marker'];
const BOLD = { montserrat: true, comfortaa: true, caveat: true };
const UNITS = { mm: 1, cm: 10, in: 25.4 };

const state = {
    // A small gift by default: its net fits ONE landscape A4 — the first thing
    // a visitor prints should not be a four-sheet puzzle.
    type: 'tuck', L: 70, W: 50, H: 40, unit: 'mm', card: false,
    colour: '#e63b2e', pattern: 'dots', line: false, label: '', font: 'montserrat', deco: 'label', charTheme: 'winter', charId: null,
    paper: 'a4', view: '3d',
};

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const fmt = (v, d = 1) => (Math.round(v * Math.pow(10, d)) / Math.pow(10, d)).toLocaleString((window.__intlLocale && window.__intlLocale()) || undefined);

/* ── label glyphs ───────────────────────────────────────────────────── */

let fontReady = null, labelCache = new Map();
async function labelPath(text) {
    const TF = window.PdfTextFont;
    if (!text || !TF) return null;
    if (!fontReady) fontReady = TF.load().catch(e => { fontReady = null; throw e; });
    await fontReady;
    if (TF.needsCjk && TF.needsCjk(text) && TF.loadCjk) await TF.loadCjk();
    const face = TF.canRender(text, state.font) ? state.font : (TF.pick(text) || 'noto');
    const key = face + '|' + text;
    if (!labelCache.has(key)) labelCache.set(key, TF.outline(face, text, 100, { bold: !!BOLD[face] }).then(r => (r && r.path) || null).catch(e => { labelCache.delete(key); throw e; }));
    return labelCache.get(key);
}

/* ── model ──────────────────────────────────────────────────────────── */

let built = null, seq = 0;
async function model() {
    const my = ++seq;
    const net = boxNet(state.type, { L: state.L, W: state.W, H: state.H }, { card: state.card });
    let lp = null, fontFailed = false;
    if (state.deco === 'label' && state.label.trim()) {
        try { lp = await labelPath(state.label.trim()); } catch (e) { fontFailed = true; }
    }
    if (my !== seq) return null;
    let charPainted = null;
    if (state.deco === 'char') {
        const th = THEMES.find(x => x.id === state.charTheme) || THEMES[0];
        const id = state.charId && (th.items || []).includes(state.charId) ? state.charId : (th.items || ['hare'])[0];
        charPainted = paint(build(id, { format: 'band' }), { mode: state.line ? 'line' : 'color', cut: 'detail', scale: 1 });
    }
    const names = { box: t('piece_box'), tray: t('piece_tray'), lid: t('piece_lid'), pillow: t('piece_box'), pyramid: t('piece_box') };
    const pieces = net.pieces.map((p, i) => Object.assign(p, {
        // Only the piece's name goes into the free corner of its box: with the
        // size the caption ran over the corner tab of a lid (05.11 print).
        caption: names[p.id],
        ops: pieceOps(p, {
            colour: state.colour, pattern: state.pattern, line: state.line, seed: 7 + i,
            label: (p.label || (net.type !== 'lid')) && lp ? { d: lp } : null,
            char: (p.label || net.type !== 'lid') ? charPainted : null,
        }),
    }));
    return { net, pieces, lp, fontFailed };
}

/* ── render ─────────────────────────────────────────────────────────── */

async function render() {
    const m = await model();
    if (!m) return;
    built = m;
    const box = $('gxPreview');
    if (state.view === '3d') {
        box.innerHTML = isoSvg(m.net, { colour: state.colour, line: state.line }, m.lp);
    } else {
        // all pieces side by side, true proportions
        let x = 0, html = '';
        const gap = 12, H = Math.max(...m.pieces.map(p => p.h));
        for (const p of m.pieces) {
            const painted = { ops: p.ops, box: { x0: p.box.x0 - 2, y0: p.box.y0 - 2, x1: p.box.x1 + 2, y1: p.box.y1 + 2 }, scale: 1 };
            html += toSvg(painted, {}).replace('<svg ', '<svg x="' + x + '" y="0" width="' + (p.w + 4) + '" height="' + (p.h + 4) + '" ');
            x += p.w + 4 + gap;
        }
        box.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + (x - gap) + ' ' + (H + 4) + '" role="img" aria-label="' + esc(t('view_net')) + '">' + html + '</svg>';
    }
    const plan = planBox(m.pieces, state.paper);
    const flat = m.pieces.map(p => fmt(p.w / 10) + ' × ' + fmt(p.h / 10)).join(' + ');
    $('gxCap').innerHTML = '<span><b>' + esc(t('flat_line', 'Flat: :s cm').replace(':s', flat)) + '</b></span><span>'
        + esc(t('sheets_line', 'Sheets: :n').replace(':n', plan.sheets)) + '</span>'
        + (plan.tiled.length ? '<span>' + esc(t('tiled_line')) + '</span>' : '');
    $('gxFontWarn').hidden = !m.fontFailed;
    if (m.fontFailed) { clearTimeout(render._r); render._r = setTimeout(schedule, 4000); }
    $('gxSumSize').textContent = t('type_' + state.type) + ' · ' + [state.L, state.W, state.H].map(v => fmt(v / UNITS[state.unit], 1)).join(' × ') + ' ' + t('u_' + state.unit);
    $('gxSumLook').textContent = state.deco === 'label' ? state.label.slice(0, 20) : t('deco_' + state.deco);
    $('gxHRow').hidden = state.type === 'pillow';
    $('gxCharRow').hidden = state.deco !== 'char';
    $('gxLabelRow').hidden = state.deco !== 'label';
}
let timer = 0;
const schedule = () => { clearTimeout(timer); timer = setTimeout(() => render().catch(err => { if (window.__handleError) window.__handleError(err, 'giftbox_render', { say: t('err_render') }); }), 100); };

/* ── controls ───────────────────────────────────────────────────────── */

function segOn(attr, v) {
    document.querySelectorAll('[' + attr + ']').forEach(b => { const on = b.getAttribute(attr) === v; b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); });
}
const TS = () => window.__toolSettings;
const remember = (k, v) => { try { if (TS()) TS().save(SLUG, k, v); } catch (e) { } };
const recall = (k, d) => { try { return TS() ? TS().load(SLUG, k, d) : d; } catch (e) { return d; } };

function readDims() {
    const k = UNITS[state.unit];
    for (const d of ['L', 'W', 'H']) {
        const v = parseFloat(String($('gx' + d).value).replace(',', '.'));
        if (isFinite(v) && v > 0) state[d] = Math.min(1000, Math.max(5, v * k));
    }
}
function writeDims() {
    const k = UNITS[state.unit];
    for (const d of ['L', 'W', 'H']) $('gx' + d).value = Math.round(state[d] / k * (state.unit === 'mm' ? 1 : 10)) / (state.unit === 'mm' ? 1 : 10);
}

function renderChoices() {
    $('gxColours').innerHTML = CRAYONS.map(c => '<button type="button" class="gx-cr' + (c === state.colour ? ' on' : '') + '" data-gxcolour="' + c + '" style="background:' + c + '" aria-label="' + c + '" aria-pressed="' + (c === state.colour) + '"></button>').join('');
    $('gxPatterns').innerHTML = PATTERNS.map(p => '<button type="button" class="tc-seg-b' + (p === state.pattern ? ' on' : '') + '" data-gxpat="' + p + '">' + esc(t('pat_' + p)) + '</button>').join('');
    $('gxPresets').innerHTML = Object.keys(PRESETS).map(p => '<button type="button" class="gx-pre" data-gxpre="' + p + '">' + esc(t('pre_' + p)) + '</button>').join('');
    const th = THEMES.filter(x => x.items);
    $('gxCharTheme').innerHTML = th.map(x => '<option value="' + x.id + '"' + (x.id === state.charTheme ? ' selected' : '') + '>' + x.icon + ' ' + esc(t('theme_' + x.id)) + '</option>').join('');
    const items = (THEMES.find(x => x.id === state.charTheme) || th[0]).items || [];
    $('gxChar').innerHTML = items.map(id => '<option value="' + id + '"' + (id === state.charId ? ' selected' : '') + '>' + esc(t('m_' + id, id)) + '</option>').join('');
}

async function renderFonts() {
    const TF = window.PdfTextFont;
    let usable = FONTS;
    if (TF) { try { if (!fontReady) fontReady = TF.load(); await fontReady; usable = FONTS.filter(id => TF.canRender(state.label || 'A', id)); } catch (e) { } }
    if (!usable.length) usable = ['noto'];
    if (!usable.includes(state.font)) state.font = usable[0];
    $('gxFont').innerHTML = usable.map(id => { const m = TF && TF.faceMeta ? TF.faceMeta(id) : null; return '<option value="' + id + '"' + (id === state.font ? ' selected' : '') + '>' + esc((m && m.label) || id) + '</option>'; }).join('');
}

/* ── output ─────────────────────────────────────────────────────────── */

function outName(ext) {
    const raw = t('file_prefix', 'box') + '-' + t('type_' + state.type) + '-' + [state.L, state.W, state.H].map(v => Math.round(v)).join('x') + 'mm';
    if (window.__outputName) return window.__outputName.join([window.__outputName.sanitize(raw)], ext);
    return raw.replace(/[^\w\-]+/g, '-') + '.' + ext;
}
const limitKey = () => 'set:' + state.type + ':' + [state.L, state.W, state.H].map(v => Math.round(v)).join('x');

let busy = false;
async function save(kind) {
    if (busy) return;
    if (!built || built.fontFailed) { try { await render(); } catch (e) { } }
    if (!built) return;
    if (built.fontFailed && kind === 'pdf') { if (window.__toast) window.__toast(t('warn_font'), 'warning'); return; }
    const gate = window.__toolLimit && window.__toolLimit.begin(SLUG, limitKey());
    if (window.__toolLimit && !gate) return;
    busy = true;
    const btn = $(kind === 'pdf' ? 'gxDlPdf' : 'gxDlSvg'); const was = btn.textContent;
    btn.disabled = true; btn.textContent = t('building', '…');
    try {
        let blob, name;
        if (kind === 'pdf') {
            const n = built.net;
            const size = fmt(n.L / 10) + ' × ' + fmt(n.W / 10) + (state.type === 'pillow' ? '' : ' × ' + fmt(n.H / 10)) + ' ' + t('u_cm');
            const r = await buildBoxPdf({ paper: state.paper, pieces: built.pieces, footer: size + ' — ' + t('ins_' + state.type), rulerCaption: t('pdf_ruler', '50 mm'), tileText: t('pdf_tile'), title: outName('pdf') });
            blob = r.blob; name = outName('pdf');
        } else {
            blob = new Blob([plotterSvg(built.net)], { type: 'image/svg+xml' }); name = outName('svg');
        }
        btn.disabled = false; btn.textContent = was;    // the file exists; the door may hold it for a second tap
        const ok = await window.__downloadFile(blob, name);
        if (gate) { if (ok !== false) gate.commit(); else gate.abort(); }
        if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, f: kind, type: state.type, L: Math.round(state.L), W: Math.round(state.W), H: Math.round(state.H), deco: state.deco, line: state.line ? 1 : 0 });
    } catch (err) {
        if (gate) gate.abort();
        if (window.__handleError) window.__handleError(err, 'giftbox_' + kind, { say: t('err_pdf') });
    } finally { busy = false; btn.disabled = false; btn.textContent = was; }
}

/* ── wiring ─────────────────────────────────────────────────────────── */

function bind() {
    document.addEventListener('click', e => {
        const el = e.target.closest('[data-gxtype],[data-gxview],[data-gxcolour],[data-gxpat],[data-gxpre],[data-gxdeco],[data-gxmode],#gxDlPdf,#gxDlSvg');
        if (!el || !el.closest('.gx')) return;
        const d = el.dataset;
        if (d.gxtype) { state.type = d.gxtype; segOn('data-gxtype', d.gxtype); remember('type', d.gxtype); }
        else if (d.gxview) { state.view = d.gxview; segOn('data-gxview', d.gxview); }
        else if (d.gxcolour) { state.colour = d.gxcolour; $('gxColourPick').value = d.gxcolour; renderChoices(); }
        else if (d.gxpat) { state.pattern = d.gxpat; renderChoices(); }
        else if (d.gxpre) { const v = PRESETS[d.gxpre]; [state.L, state.W, state.H] = v; writeDims(); }
        else if (d.gxdeco) { state.deco = d.gxdeco; segOn('data-gxdeco', d.gxdeco); }
        else if (d.gxmode) { state.line = d.gxmode === 'line'; segOn('data-gxmode', d.gxmode); }
        else if (el.id === 'gxDlPdf') { save('pdf'); return; }
        else if (el.id === 'gxDlSvg') { save('svg'); return; }
        schedule();
    });
    for (const d of ['L', 'W', 'H']) $('gx' + d).addEventListener('input', () => { readDims(); schedule(); });
    $('gxUnit').addEventListener('change', e => { state.unit = e.target.value; remember('unit', state.unit); writeDims(); schedule(); });
    $('gxCard').addEventListener('change', e => { state.card = e.target.checked; schedule(); });
    $('gxColourPick').addEventListener('input', e => { state.colour = e.target.value; schedule(); });
    $('gxLabel').addEventListener('input', e => { state.label = e.target.value; schedule(); clearTimeout(bind._f); bind._f = setTimeout(renderFonts, 400); });
    $('gxFont').addEventListener('change', e => { state.font = e.target.value; schedule(); });
    $('gxCharTheme').addEventListener('change', e => { state.charTheme = e.target.value; state.charId = null; renderChoices(); schedule(); });
    $('gxChar').addEventListener('change', e => { state.charId = e.target.value; schedule(); });
    $('gxPaperSel').addEventListener('change', e => { state.paper = e.target.value; remember('paper', state.paper); schedule(); });
}

function init() {
    const ty = recall('type', null); if (ty && TYPES.includes(ty)) state.type = ty;
    const u = recall('unit', null); if (u && UNITS[u]) state.unit = u;
    else if (/-(US)$/i.test(navigator.language || '')) state.unit = 'in';
    const sp = recall('paper', null);
    state.paper = sp && PAPERS[sp] ? sp : (/-(US|CA)$/i.test(navigator.language || '') ? 'letter' : 'a4');
    $('gxPaperSel').value = state.paper; $('gxUnit').value = state.unit;
    state.label = t('default_label', '');
    $('gxLabel').value = state.label;
    segOn('data-gxtype', state.type); segOn('data-gxview', state.view); segOn('data-gxdeco', state.deco); segOn('data-gxmode', 'color');
    $('gxColourPick').value = state.colour;
    writeDims(); renderChoices(); renderFonts(); bind(); schedule();
    const warm = () => { document.querySelector('.gx').removeEventListener('pointerdown', warm, true); ensurePdfLib().catch(() => { }); };
    document.querySelector('.gx').addEventListener('pointerdown', warm, true);
    try { if (window.__stage) window.__stage('ready'); } catch (e) { }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
